import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark,
  Plus,
  Trash2,
  MapPin,
  ShieldCheck,
  AlertTriangle,
  ArrowRight,
  Lock,
  ExternalLink,
  Bell,
  BellRing,
  Volume2,
  VolumeX,
  Play,
  CheckCircle,
} from 'lucide-react';
import { US_LOCATIONS, findLocation, USLocation } from '../services/locationData';
import { NWSAdapter } from '../services/nwsAdapter';
import { NotificationService } from '../services/notificationService';
import { NormalizedAlert } from '../types/alert';
import { AlertCard } from '../components/alerts/AlertCard';
import { DisclaimerBanner } from '../components/common/DisclaimerBanner';

interface SavedSpot {
  id: string;
  label: string; // e.g., "Home", "Office", "Parents"
  locationCode: string;
  locationName: string;
}

const STORAGE_KEY = 'sn_saved_locations';

export const SavedLocationsPage: React.FC = () => {
  const [savedSpots, setSavedSpots] = useState<SavedSpot[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {
      // fallback
    }
    // Default starter set
    return [
      { id: '1', label: 'Home', locationCode: 'TX', locationName: 'Texas' },
      { id: '2', label: 'Office', locationCode: 'CA', locationName: 'California' },
      { id: '3', label: 'Family / Parents', locationCode: 'FL', locationName: 'Florida' },
    ];
  });

  const [newLabel, setNewLabel] = useState('');
  const [selectedState, setSelectedState] = useState('TX');
  const [alertsBySpot, setAlertsBySpot] = useState<Record<string, NormalizedAlert[]>>({});
  const [loading, setLoading] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [notifPerm, setNotifPerm] = useState<NotificationPermission>('default');
  const [testSent, setTestSent] = useState(false);

  useEffect(() => {
    setAudioEnabled(NotificationService.isAudioEnabled());
    setNotifPerm(NotificationService.getPermission());
  }, []);

  // Save to localStorage whenever savedSpots changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedSpots));
    } catch (e) {
      console.warn('Could not persist saved locations:', e);
    }
  }, [savedSpots]);

  // Load alerts for all saved spots
  useEffect(() => {
    const fetchSavedSpotAlerts = async () => {
      setLoading(true);
      const results: Record<string, NormalizedAlert[]> = {};

      for (const spot of savedSpots) {
        try {
          const res = await NWSAdapter.fetchActiveAlerts({ stateCode: spot.locationCode });
          results[spot.id] = res.alerts;
        } catch {
          results[spot.id] = [];
        }
      }

      setAlertsBySpot(results);
      setLoading(false);
    };

    if (savedSpots.length > 0) {
      fetchSavedSpotAlerts();
    }
  }, [savedSpots]);

  const handleAddSpot = (e: React.FormEvent) => {
    e.preventDefault();
    const loc = US_LOCATIONS.find((l) => l.code === selectedState);
    if (!loc) return;

    const newSpot: SavedSpot = {
      id: Date.now().toString(),
      label: newLabel.trim() || loc.name,
      locationCode: loc.code,
      locationName: loc.name,
    };

    setSavedSpots([...savedSpots, newSpot]);
    setNewLabel('');
  };

  const handleDeleteSpot = (id: string) => {
    setSavedSpots(savedSpots.filter((s) => s.id !== id));
  };

  const handleToggleAudio = () => {
    const next = !audioEnabled;
    setAudioEnabled(next);
    NotificationService.setAudioEnabled(next);
  };

  const handleEnablePush = async () => {
    const granted = await NotificationService.requestPermission();
    setNotifPerm(NotificationService.getPermission());
    if (granted) {
      NotificationService.playChime('info');
    }
  };

  const handleTestChimeAndNotification = () => {
    NotificationService.playChime('warning');
    if (notifPerm === 'granted') {
      try {
        new Notification('🔔 SignalNear Alert Test', {
          body: 'Emergency alert dispatch operational for your saved locations.',
          icon: '/favicon.ico',
        });
      } catch (e) {
        console.warn('Notification blocked:', e);
      }
    }
    setTestSent(true);
    setTimeout(() => setTestSent(false), 3000);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {/* Header */}
      <div className="max-w-3xl space-y-2">
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-mono font-bold border border-emerald-200">
          <Lock className="w-3.5 h-3.5" />
          <span>100% PRIVATE • CLIENT-SIDE ONLY • ZERO TRACKING</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-navy-950 font-sans">
          Family &amp; Multi-Location Disruption Radar
        </h1>
        <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-sans">
          Monitor your home, workplace, children’s school, or parents' location simultaneously without creating an account or transmitting your exact coordinates. All preferences are stored strictly inside your private browser cache.
        </p>
      </div>

      {/* Real-Time Push Notification & Sound Chime Settings */}
      <div className="bg-navy-950 text-white border border-slate-800 rounded-2xl p-5 sm:p-6 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Bell className="w-4 h-4 text-cyan-400" />
              <span>Live Alert Dispatch &amp; Audio Broadcast</span>
            </h2>
            <p className="text-xs text-slate-400">
              Receive automatic browser notifications and audible alarms whenever a new Extreme or Severe bulletin is issued for your saved areas.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleToggleAudio}
              className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border ${
                audioEnabled
                  ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
              }`}
            >
              {audioEnabled ? <Volume2 className="w-4 h-4 text-cyan-400" /> : <VolumeX className="w-4 h-4" />}
              <span>Audio Chime: {audioEnabled ? 'ON' : 'MUTED'}</span>
            </button>

            {notifPerm !== 'granted' ? (
              <button
                onClick={handleEnablePush}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl text-xs font-bold hover:from-blue-500 hover:to-cyan-500 transition-all flex items-center gap-1.5 shadow-md"
              >
                <BellRing className="w-4 h-4" />
                <span>Enable Browser Alerts</span>
              </button>
            ) : (
              <span className="px-3 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded-xl text-xs font-bold flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span>Browser Alerts Active</span>
              </span>
            )}

            <button
              onClick={handleTestChimeAndNotification}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl text-xs font-mono transition-colors flex items-center gap-1"
            >
              <Play className="w-3 h-3 text-cyan-400" />
              <span>{testSent ? 'Sound Dispatched!' : 'Test Sound'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Add Location Form */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
        <h2 className="text-sm font-bold text-navy-950 uppercase tracking-wider font-mono flex items-center gap-2">
          <Plus className="w-4 h-4 text-blue-600" />
          <span>Add Monitored Location</span>
        </h2>

        <form onSubmit={handleAddSpot} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label htmlFor="spot-label" className="sr-only">Location Label</label>
            <input
              id="spot-label"
              type="text"
              placeholder="e.g. Home, Office, Daughter's College..."
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="w-full px-4 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-sans"
            />
          </div>

          <div className="w-full sm:w-64">
            <label htmlFor="spot-state" className="sr-only">Select State</label>
            <select
              id="spot-state"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-4 py-2.5 text-xs sm:text-sm bg-slate-50 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-sans font-medium"
            >
              {US_LOCATIONS.map((loc) => (
                <option key={loc.code} value={loc.code}>
                  {loc.name} ({loc.code})
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="px-6 py-2.5 bg-navy-950 hover:bg-navy-900 text-white font-bold rounded-xl text-xs sm:text-sm transition-all shadow-md flex items-center justify-center gap-1.5 flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            <span>Save Location</span>
          </button>
        </form>
      </div>

      {/* Safety Notice */}
      <DisclaimerBanner />

      {/* Monitored Locations Grid */}
      <div className="space-y-6">
        <h2 className="text-xl font-black text-navy-950">
          Your Monitored Locations ({savedSpots.length})
        </h2>

        {savedSpots.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-500 text-sm">
            You haven't saved any locations yet. Add a location above to monitor your family or workplace.
          </div>
        ) : (
          <div className="space-y-6">
            {savedSpots.map((spot) => {
              const spotAlerts = alertsBySpot[spot.id] || [];
              const severeCount = spotAlerts.filter(
                (a) => a.severity === 'Extreme' || a.severity === 'Severe'
              ).length;

              return (
                <div
                  key={spot.id}
                  className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs space-y-4"
                >
                  {/* Spot Header */}
                  <div className="p-5 bg-slate-50 border-b border-slate-200/80 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-navy-950 text-white flex items-center justify-center font-bold font-mono text-sm shadow-xs">
                        {spot.locationCode}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-navy-950 text-base">{spot.label}</h3>
                          <span className="text-xs text-slate-500">({spot.locationName})</span>
                        </div>
                        <div className="text-xs font-mono mt-0.5">
                          {spotAlerts.length === 0 ? (
                            <span className="text-emerald-700 font-semibold flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 inline" />
                              Zero Active Weather Bulletins
                            </span>
                          ) : (
                            <span className="text-slate-600">
                              <strong className={severeCount > 0 ? 'text-red-700' : 'text-navy-950'}>
                                {spotAlerts.length}
                              </strong>{' '}
                              Active Alerts ({severeCount} High-Severity)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Link
                        to={`/location/${spot.locationCode}`}
                        className="px-3 py-1.5 text-xs font-bold text-navy-950 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-1"
                      >
                        <span>Full State Radar</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                      <button
                        onClick={() => handleDeleteSpot(spot.id)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove saved spot"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Spot Active Alerts */}
                  <div className="p-5 pt-0">
                    {loading && !spotAlerts ? (
                      <div className="h-24 bg-slate-100 rounded-xl animate-pulse" />
                    ) : spotAlerts.length === 0 ? (
                      <div className="py-6 text-center text-xs text-slate-500 font-sans">
                        No active National Weather Service warnings or watches currently issued for {spot.locationName}.
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {spotAlerts.slice(0, 3).map((alert) => (
                          <AlertCard key={alert.id} alert={alert} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
